import React, { useState, useRef } from "react";
import ModelSelector from "./ModelSelector";
import styles from "./ChatWorkspace.module.scss";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";

export default function ChatInput({
  models,
  selectedModel,
  onModelChange,
  onSend,
  onFilesSelected,
  attachments,
  onRemoveAttachment,
  disabled,
  placeholder = "Write your message....",
  suggestionChips = null,
  compact = false,
}) {
  // When compact, only the input bar is visible (no chips, hint, or attachment list)
  const chips =
    compact ? [] : (suggestionChips && suggestionChips.length > 0 ? suggestionChips : []);
  const [value, setValue] = useState("");
  const [dragging, setDragging] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);
  const fileInputRef = useRef(null);

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue("");
  };

  return (
    <div className={`${styles.composer} ${compact ? styles.composerCompact : ""}`}>
      {!compact && chips.length > 0 && (
        <div className={styles.suggestionChips}>
          {chips.map((label) => (
            <button
              key={label}
              type="button"
              className={styles.suggestionChip}
              onClick={() => onSend(label)}
              disabled={disabled}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {!compact && attachments?.length > 0 && (
        <div className={styles.attachmentList}>
          {attachments.map((attachment) => (
            <span key={attachment.localId || attachment.id} className={styles.attachmentChip}>
              {attachment.name}
              <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none ms-1"
                onClick={() => onRemoveAttachment?.(attachment)}
              >
                remove
              </button>
            </span>
          ))}
        </div>
      )}

      {!compact && !hintDismissed && (
        <div className={styles.composerHint}>
          <span>Select a suggestion above or type to get started.</span>
          <button
            type="button"
            className={styles.composerHintDismiss}
            onClick={() => setHintDismissed(true)}
            aria-label="Dismiss hint"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
      )}

      <div
        className={styles.composerBar}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const files = Array.from(e.dataTransfer.files || []);
          if (files.length) onFilesSelected?.(files);
        }}
        style={{ borderColor: dragging ? "var(--accent)" : undefined }}
      >
        <ModelSelector
          models={models}
          value={selectedModel}
          onChange={onModelChange}
          disabled={disabled}
          compact={true}
        />
        <input
          type="text"
          className={styles.composerInput}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <div className={styles.composerActions}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="d-none"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) onFilesSelected?.(files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className={styles.composerIconBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            aria-label="Attach file"
          >
            <AttachFileIcon sx={{ fontSize: 20 }} />
          </button>
          <button
            type="button"
            className={`${styles.composerIconBtn} ${styles.composerIconBtnSend}`}
            onClick={submit}
            disabled={disabled || !value.trim()}
            aria-label="Send message"
          >
            <SendIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
      </div>
    </div>
  );
}
